import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { ChancenDialogTimeMatrix } from '@chancenwerk/client/components/dialogs/chancen-dialog-time-matrix.js';
import '@chancenwerk/components/chancen-component-choose-digital-courses.js';
import '@chancenwerk/components/chancen-component-choose-exam-prep.js';
import '@chancenwerk/components/chancen-component-choose-intensive-courses.js';
import type { Course } from '@chancenwerk/db/courses/Course.js';
import { CourseDigital, Subject } from '@chancenwerk/db/courses/CourseDigital.js';
import type { CourseIntensive } from '@chancenwerk/db/courses/CourseIntensive.js';
import type { CourseIntensiveSingle } from '@chancenwerk/db/courses/CourseIntensiveSingle.js';
import '@chancenwerk/design/components/cr-nospace-toggle-content.js';
import type { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, switchMap, takeUntil, type Subscription } from 'rxjs';
import ImageClientsSupport from './../assets/Classroom-med-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

/**
 * @customElement anmeldung-component-formular-lkk-intensive-offer-help
 */
@customElement('anmeldung-component-formular-lkk-intensive-offer-help')
export class AnmeldungPageRegistrationLKKOfferHelp extends AnmeldungPageRegistrationBase {
  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;

  override render() {
    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-flex space="lg">
              <cr-flex space="md" padding="md" ${css({ borderLeft: `5px solid ${this.colors.accent}` })}>
                <cr-h4 ${css({ fontWeight: 'bold' })}>Angebot 1</cr-h4>
                <cr-text>Hol dir deinen <b>wöchentlichen</b> Intensivkurs in den Fächern deiner Wahl.</cr-text>
              </cr-flex>

              <cr-flex space="md" padding="md" ${css({ borderLeft: `5px solid ${this.colors.accent}` })}>
                <cr-h4 ${css({ fontWeight: 'bold' })}>Angebot 2</cr-h4>
                <cr-text
                  >Such dir Intensivkurse nach Themen aus (<span ${css({ fontWeight: 'bold' })}>Einzeltermine</span
                  >).</cr-text
                >
              </cr-flex>

              ${this.student.value.schoolInformation.jahrgangsstufe === 10
                ? html` <cr-flex space="md" padding="md" ${css({ borderLeft: `5px solid ${this.colors.accent}` })}>
                    <cr-h4 ${css({ fontWeight: 'bold' })}>Angebot 3</cr-h4>
                    <cr-text>
                      Sichere dir schon jetzt deinen Platz für die Prüfungsvorbereitung inkl. Prüfungssimulation (<span
                        ${css({ fontWeight: 'bold' })}
                        >4 Samstage</span
                      >) Wähle für dich passende Intensivkurse aus.
                    </cr-text>
                  </cr-flex>`
                : nothing}
              <cr-text>
                Du kannst nach Bedarf gerne an mehreren Angeboten teilnehmen. Klicke dich auf den nächsten Seiten durch
                unsere Angebote durch.
              </cr-text>
            </cr-flex>

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                @click=${async () => {
                  this.form.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}

/**
 * @customElement anmeldung-component-formular-lkk-intensive-offer-1
 */
@customElement('anmeldung-component-formular-lkk-intensive-offer-1')
export class AnmeldungPageRegistrationLKKOffer1 extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: false }) subjects!: RXController<string[]>;
  @property({ attribute: false }) intensiveCourses!: RXController<Course[]>;
  @property({ attribute: false }) digitalCoursesChoosen: CourseDigital[] = [];
  @property({ attribute: false }) digitalCourses!: RXController<CourseDigital[]>;

  @state() private readonly _digitalCourses = new RXController<CourseDigital[]>(this, []);

  _studentSub?: Subscription;

  override connectedCallback(): void {
    super.connectedCallback();

    // ! As we pass RXController instances at the moment components that have
    // ! student.value as property won't correctly get updated. So we manually
    // ! trigger a update for now. This should be removed by doing more refactoring
    // ! of the formular steps.
    this._studentSub = this.student.observable.subscribe(async s => {
      this.requestUpdate('student');

      combineLatest([this.student.observable, this.subjects.observable])
        .pipe(
          takeUntil(this.disconnected),
          switchMap(([student, subjects]) =>
            CourseDigital.subscribeDigitialCourseSubjectAndGrade(
              subjects,
              student.schoolInformation.jahrgangsstufe ?? 0,
            )({
              _class: 1,
              _id: 1,
              weekday: 1,
              startTime: 1,
              endTime: 1,
              linkedKPFIDs: 1,
              linkedKPFs: 1,
              location: 1,
              osStudentIDs: 1,
              jahrgangsstufe: 1,
              subject: 1,
            }),
          ),
        )
        .subscribe(digitalCourses => {
          this._digitalCourses.next(digitalCourses);
        });
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._studentSub?.unsubscribe();
  }

  private _courses() {
    if (this.subjects.value.filter(Boolean).filter(x => x.length > 0).length === 0) {
      return html`<cr-flex space="md">
        <cr-text>Bitte wähle mindestens ein Wunschfach aus.</cr-text>
      </cr-flex>`;
    }

    return html` <cr-flex space="md">
        <cr-h4>Mein Intensivkurs vor Ort:</cr-h4>
        <chancen-component-choose-intensive-courses
          .student=${this.student.value}
          .intensiveCourses=${this.intensiveCourses.value.filter(c =>
            [(c as CourseIntensive).subjectOne, (c as CourseIntensive).subjectTwo]
              .filter(Boolean)
              .some(fach => this.subjects.value.includes(fach ?? '')),
          )}
          @value-picked=${(e: ValueEvent<Student>) => this.student.next(e.detail.value)}
        >
        </chancen-component-choose-intensive-courses>
      </cr-flex>

      <cr-flex space="md">
        <cr-h4>Mein digitaler Intensivkurs:</cr-h4>
        <chancen-component-choose-digital-courses
          .student=${this.student.value}
          .digitalCourses=${this._digitalCourses.value}
          .digitalCoursesChoosen=${this.digitalCoursesChoosen}
          @value-picked=${(e: ValueEvent<Student>) => this.student.next(this.student.value)}
        >
        </chancen-component-choose-digital-courses>
      </cr-flex>`;
  }

  override render() {
    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-flex space="md">
              <cr-h4 ${css({ fontWeight: 'bold' })}>Hol dir deinen wöchentlichen Intensivkurs</cr-h4>

              <cr-h4> Wunschfach für meinen Intensivkurs: </cr-h4>
              <cr-dropdown-search-selection
                clearable
                placeholder="Erstwunsch*"
                ${this.form.field('preferedFirstSubject')}
                .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
                .search=${(search?: string) =>
                  Object.values(Subject)
                    .filter(s => s !== Subject.NATURWISSENSCHAFTEN)
                    .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
                @value-picked=${e => {
                  this.subjects.value[0] = e.detail.value;
                  this.subjects.next(this.subjects.value);
                  this.student.next(this.student.value);
                  this.requestUpdate();
                }}
              ></cr-dropdown-search-selection>
              <cr-dropdown-search-selection
                clearable
                placeholder="Zweitwunsch"
                ${this.form.field('preferedSecondSubject')}
                .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
                .search=${(search?: string) =>
                  Object.values(Subject)
                    .filter(s => s !== Subject.NATURWISSENSCHAFTEN)
                    .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
                @value-picked=${e => {
                  this.subjects.value[1] = e.detail.value;
                  this.subjects.next(this.subjects.value);
                  this.student.next(this.student.value);
                  this.requestUpdate();
                }}
              ></cr-dropdown-search-selection>
              <cr-dropdown-search-selection
                clearable
                placeholder="Drittwunsch"
                ${this.form.field('preferedThirdSubject')}
                .selectable=${Object.values(Subject).filter(s => s !== Subject.NATURWISSENSCHAFTEN)}
                .search=${(search?: string) =>
                  Object.values(Subject)
                    .filter(s => s !== Subject.NATURWISSENSCHAFTEN)
                    .filter(x => x.toLowerCase().includes(search?.toLowerCase() ?? ''))}
                @value-picked=${e => {
                  this.subjects.value[2] = e.detail.value;
                  this.subjects.next(this.subjects.value);
                  this.student.next(this.student.value);
                  this.requestUpdate();
                }}
              ></cr-dropdown-search-selection>
            </cr-flex>

            ${this._courses()}

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                mode="outline"
                @click=${async () => {
                  const student = await ChancenDialogTimeMatrix.showPopup<Student>({
                    props: { student: this.student.value },
                  });
                  this.student.next(student);
                  this.requestUpdate();
                }}
              >
                Keinen digitalen Intensivkurs gefunden?
              </cr-button>
              <cr-button
                @click=${async () => {
                  const wishes = [
                    this.form.document?.preferedFirstSubject,
                    this.form.document?.preferedSecondSubject,
                    this.form.document?.preferedThirdSubject,
                  ].filter(x => !!x && x !== '');

                  // if (
                  //   this.student.value.mitgliedschaft === Mitgliedschaften.HILFE_GEBEN_NEHMEN &&
                  //   wishes.length === 0
                  // ) {
                  //   await ChromiumSnackbar.show('Bitte wähle mindestens ein Wunschfach aus.');
                  //   return;
                  // }

                  this.form.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}

/**
 * @customElement anmeldung-component-formular-lkk-intensive-offer-2
 */
@customElement('anmeldung-component-formular-lkk-intensive-offer-2')
export class AnmeldungPageRegistrationLKKOffer2 extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: false }) intensiveCourses!: RXController<Maybe<CourseIntensiveSingle[]>>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';

  _studentSub?: Subscription;

  override connectedCallback(): void {
    super.connectedCallback();

    // ! As we pass RXController instances at the moment components that have
    // ! student.value as property won't correctly get updated. So we manually
    // ! trigger a update for now. This should be removed by doing more refactoring
    // ! of the formular steps.
    this._studentSub = this.student.observable.subscribe(async s => {
      this.requestUpdate('student');
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._studentSub?.unsubscribe();
  }

  override render() {
    const subjects = [...new Set((this.intensiveCourses.value ?? []).map(c => c.subject))];

    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-h4>
              Such dir Intensivkurse nach Themen deiner Wahl aus.
              <span ${css({ fontWeight: 'bold' })}>(Einzeltermine)</span>
            </cr-h4>

            <cr-text>Wähle so viele Termine aus, wie du möchtest.</cr-text>

            ${repeat(
              subjects,
              s => s,
              s => {
                return html` <cr-nospace-toggle-content .label=${s}>
                  <chancen-component-choose-intensive-courses
                    .student=${this.student.value}
                    .intensiveCourses=${this.intensiveCourses.value.filter(c => c.subject === s)}
                    .studentField=${'intensiveCoursesSingleChoosen'}
                    .maximumChoosen=${1000}
                    @value-picked=${(e: ValueEvent<Student>) => this.student.next(e.detail.value)}
                  >
                  </chancen-component-choose-intensive-courses>
                </cr-nospace-toggle-content>`;
              },
            )}

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                @click=${async () => {
                  this.form.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}

/**
 * @customElement anmeldung-component-formular-lkk-intensive-offer-3
 */
@customElement('anmeldung-component-formular-lkk-intensive-offer-3')
export class AnmeldungPageRegistrationLKKOffer3 extends AnmeldungPageRegistrationBase {
  @state() private readonly _size = new WindowSizeController(this);

  @property({ attribute: false }) form!: FormController<Student>;
  @property({ attribute: false }) translation!: TranslationController<any>;
  @property({ attribute: false }) student!: RXController<Student>;
  @property({ attribute: false }) choosenKPF!: RXController<KPF | undefined>;
  @property({ attribute: false }) subjects!: RXController<string[]>;
  @property({ attribute: false }) intensiveCourses!: RXController<Course[]>;
  @property({ attribute: 'school-level' }) schoolLevel: 'us' | 'os' = 'us';

  _studentSub?: Subscription;

  override connectedCallback(): void {
    super.connectedCallback();

    // ! As we pass RXController instances at the moment components that have
    // ! student.value as property won't correctly get updated. So we manually
    // ! trigger a update for now. This should be removed by doing more refactoring
    // ! of the formular steps.
    this._studentSub = this.student.observable.subscribe(async s => {
      this.requestUpdate('student');
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    this._studentSub?.unsubscribe();
  }

  override render() {
    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-h4>
              Abschlussprüfung Klasse 10: Sichere dir schon jetzt deinen Platz für die Prüfungsvorbereitung inkl.
              Prüfungssimulation <span ${css({ fontWeight: 'bold' })}>(4 Samstage)</span>
            </cr-h4>

            <cr-text>
              Wähle alle Fächer aus, in denen du bei der Vorbereitung auf deine Abschlussprüfungen unterstützt werden
              möchtest.
            </cr-text>

            <chancen-component-choose-exam-prep .student=${this.student.value}></chancen-component-choose-exam-prep>

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                @click=${async () => {
                  this.form.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}
